import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Users,
  Check,
  X,
  RefreshCw,
  Clock,
  Smartphone,
  Key,
  Copy,
  AlertCircle,
} from "lucide-react";
import { useWaiterSessionsManagement } from "@/hooks/useWaiterSessionsManagement";
import { useDailyAccessCode } from "@/hooks/useDailyAccessCode";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

export function WaiterSessionsCard() {
  const {
    activeSessions,
    pendingRequests,
    recentSessions,
    isLoading,
    approveSession,
    rejectSession,
    revokeSession,
  } = useWaiterSessionsManagement();

  const { todayCode, isLoading: codeLoading, generateNewCode } = useDailyAccessCode();
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleApprove = async (sessionId: string) => {
    setProcessingId(sessionId);
    await approveSession(sessionId);
    setProcessingId(null);
  };

  const handleReject = async (sessionId: string) => {
    setProcessingId(sessionId);
    await rejectSession(sessionId);
    setProcessingId(null);
  };

  const handleRevoke = async (sessionId: string) => {
    setProcessingId(sessionId);
    await revokeSession(sessionId);
    setProcessingId(null);
  };

  const handleCopyCode = () => {
    if (todayCode) {
      navigator.clipboard.writeText(todayCode);
      toast.success("Código copiado!");
    }
  };

  const formatExpiresAt = (expiresAt: string | null) => {
    if (!expiresAt) return "";
    const date = new Date(expiresAt);
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const formatRelativeTime = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Garçons
          </CardTitle>
          {/* Código do dia */}
          <div className="flex items-center gap-2">
            {todayCode ? (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-lg px-3 py-1">
                  {todayCode}
                </Badge>
                <Button variant="ghost" size="icon" onClick={handleCopyCode}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={generateNewCode}
                disabled={codeLoading}
              >
                <Key className="h-4 w-4 mr-2" />
                Gerar Código
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Solicitações pendentes */}
        {pendingRequests.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">
                {pendingRequests.length} solicitação(ões) pendente(s)
              </span>
            </div>
            <div className="space-y-2">
              {pendingRequests.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {session.waiter_name || "Garçom"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelativeTime(session.requested_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(session.id)}
                      disabled={processingId === session.id}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(session.id)}
                      disabled={processingId === session.id}
                      className="h-8"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Aprovar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingRequests.length > 0 && activeSessions.length > 0 && <Separator />}

        {/* Sessões ativas */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-sm font-medium">
              {activeSessions.length} garçom(ns) ativo(s)
            </span>
          </div>

          {activeSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              Nenhum garçom com sessão ativa no momento.
            </p>
          ) : (
            <ScrollArea className="max-h-48">
              <div className="space-y-2">
                {activeSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      {session.checkin_method === "code" ? (
                        <Key className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {session.waiter_name || "Garçom"}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          até {formatExpiresAt(session.expires_at)}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRevoke(session.id)}
                      disabled={processingId === session.id}
                      className="text-destructive hover:text-destructive"
                    >
                      Revogar
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Botão para gerar novo código */}
        {todayCode && (
          <>
            <Separator />
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={generateNewCode}
              disabled={codeLoading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Gerar Novo Código
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Gerar novo código invalidará o anterior
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
